import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import { BarChart3 } from 'lucide-react';
import PaginationBar from '../../PlatformAdmin/PaginationBar';
import { DIMENSION_LABELS, dimensionCellValue, n0, formatDurationMs, formatCost } from './usageFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './UsageAnalyticsPanel.module.scss';

const METRIC_COLUMNS = [
  { key: 'calls', label: 'Calls', render: (r) => n0(r.calls) },
  { key: 'input_tokens', label: 'Input', render: (r) => n0(r.input_tokens) },
  { key: 'output_tokens', label: 'Output', render: (r) => n0(r.output_tokens) },
  { key: 'total_tokens', label: 'Total tokens', render: (r) => n0(r.total_tokens) },
  { key: 'tool_calls', label: 'Tool calls', render: (r) => n0(r.tool_calls) },
  { key: 'errors', label: 'Errors', render: (r) => n0(r.errors) },
  { key: 'avg_duration_ms', label: 'Avg duration', render: (r) => formatDurationMs(r.avg_duration_ms) },
  {
    key: 'estimated_cost',
    label: 'Est. cost',
    render: (r) => {
      const cost = formatCost(r.estimated_cost, r.cost_coverage_pct);
      return cost.tooltip ? (
        <Tooltip title={cost.tooltip}>
          <span>{cost.text}</span>
        </Tooltip>
      ) : (
        cost.text
      );
    },
  },
];

/**
 * The main grouped table (spec §5 `/usage/breakdown`). Columns are derived from
 * the `group_by` the response echoes back, plus fixed metric columns. Rows are
 * clickable to drill down into the individual runs behind them (spec §9). The
 * footer renders the whole-period `totals`, not the current page, per §5.
 */
const UsageBreakdownTable = ({
  breakdown,
  loading,
  offset,
  limit,
  onOffsetChange,
  onRowClick,
  companyLabelFn = null,
  emptyMessage = null,
}) => {
  const groupBy = breakdown?.group_by || [];
  const grain = breakdown?.range?.granularity || 'day';
  const items = breakdown?.items || [];
  const total = breakdown?.total ?? items.length;

  const gridTemplateColumns = useMemo(
    () => `repeat(${Math.max(groupBy.length, 1)}, minmax(140px, 1.2fr)) repeat(${METRIC_COLUMNS.length}, minmax(96px, 1fr))`,
    [groupBy.length]
  );

  if (loading && !breakdown) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <Box className={sharedStyles.emptyState}>
        <BarChart3 className={sharedStyles.emptyIcon} size={56} />
        <Typography variant="h6">No usage in this period</Typography>
        <Typography variant="body2">{emptyMessage || 'Nothing matches the current filters.'}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box className={sharedStyles.tableCard} sx={{ overflowX: 'auto' }}>
        <Box className={styles.breakdownRow} style={{ gridTemplateColumns }}>
          {groupBy.map((dim) => (
            <span key={dim} className={styles.breakdownHeadCell}>
              {DIMENSION_LABELS[dim] || dim}
            </span>
          ))}
          {METRIC_COLUMNS.map((col) => (
            <span key={col.key} className={styles.breakdownHeadCell}>
              {col.label}
            </span>
          ))}
        </Box>

        {items.map((row, idx) => (
          <Box
            key={idx}
            className={`${styles.breakdownRow} ${styles.breakdownBodyRow}`}
            style={{ gridTemplateColumns }}
            onClick={() => onRowClick?.(row)}
          >
            {groupBy.map((dim) => (
              <span key={dim} className={styles.breakdownCell}>
                {dimensionCellValue(dim, row, grain, companyLabelFn)}
              </span>
            ))}
            {METRIC_COLUMNS.map((col) => (
              <span key={col.key} className={styles.breakdownCell}>
                {col.render(row)}
              </span>
            ))}
          </Box>
        ))}

        {breakdown?.totals && (
          <Box className={`${styles.breakdownRow} ${styles.breakdownFooterRow}`} style={{ gridTemplateColumns }}>
            <span className={styles.breakdownCell} style={{ gridColumn: `span ${Math.max(groupBy.length, 1)}`, fontWeight: 700 }}>
              Total ({total} group{total === 1 ? '' : 's'} · whole period)
            </span>
            {METRIC_COLUMNS.map((col) => (
              <span key={col.key} className={styles.breakdownCell} style={{ fontWeight: 700 }}>
                {col.render(breakdown.totals)}
              </span>
            ))}
          </Box>
        )}
      </Box>

      <PaginationBar total={total} limit={limit} offset={offset} loading={loading} unit="groups" onOffsetChange={onOffsetChange} />
    </>
  );
};

export default UsageBreakdownTable;
